import * as React from 'react';
import { useState } from 'react';
import { Paper, Typography, Box } from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import BusinessIcon from '@mui/icons-material/Business';
import PublicIcon from '@mui/icons-material/Public';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { Card, CardContent } from "@mui/material";
import {
    useCreatePath,
    useRecordContext,
    Link,
    useTheme,
} from 'react-admin';
import { Fund } from '../../types/types';
import {
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
    AreaChart,
    Area,
} from 'recharts';

import { FundChart } from "./FundChart";

type FundStatsType = {
    ytdReturn: number;
    dailyChange: number;
    currentValuePerShare: number;
    volume: number;
};


// Fixed daily data that won't change on re-renders
const getDailyData = () => {
    const fixedChanges = [
        0.32, -0.18, 0.27, 0.15, -0.23, -0.12, 0.42, 0.19, -0.08, 0.35,
        0.26, -0.15, -0.21, 0.38, 0.22, -0.11, 0.33, 0.17, -0.19, 0.24,
        0.41, -0.13, 0.29, 0.16, -0.22, 0.31, 0.18, -0.16, 0.25, 0.37
    ];

    const data = [];
    const today = new Date();
    let currentValue = 2.5;

    for (let i = 29; i >= 0; i--) {
        const currentDate = new Date(today);
        currentDate.setDate(today.getDate() - i);

        const month = currentDate.getMonth() + 1;
        const day = currentDate.getDate();
        const dateStr = `${month}/${day}`;

        currentValue += fixedChanges[29 - i];

        data.push({
            date: dateStr,
            value: parseFloat(currentValue.toFixed(2))
        });
    }

    return data;
};

// Card Header component with fund name and current price
const FundCardHeader = ({ fund, stats }: { fund: Fund; stats: FundStatsType }) => (
    <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={1}
    >
        <Box display="flex" alignItems="center" gap={1}>
            <ArrowForwardIosIcon color="primary" />
            <Typography variant="h6" fontWeight="bold">
                {fund.name}
            </Typography>
        </Box>

        <Box textAlign="right">
            <Typography variant="h5" fontWeight="bold" color="primary">
                {stats.currentValuePerShare.toFixed(2)} €
            </Typography>
            <Typography
                variant="body2"
                fontWeight="medium"
                color={stats.dailyChange >= 0 ? 'green' : 'red'}
            >
                {stats.dailyChange >= 0 ? '+' : ''}{stats.dailyChange.toFixed(2)}%
            </Typography>
        </Box>
    </Box>
);

const FundStats = ({ fund, stats }: { fund: Fund; stats: FundStatsType }) => (
    <Box
        sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 2,
            mb: 2,
            px: 1
        }}
    >


        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <BusinessIcon fontSize="small" color="primary" />
            <Typography variant="caption" color="textSecondary">Corp:</Typography>
            <Typography variant="body2" fontWeight="bold">
                {fund.corporation || "Aktiengesellschaft"}
            </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <PublicIcon fontSize="small" color="primary" />
            <Typography variant="caption" color="textSecondary">Country:</Typography>
            <Typography variant="body2" fontWeight="bold">
                {fund.country || "Liechtenstein"}
            </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <TrendingUpIcon fontSize="small" color="primary" />
            <Typography variant="caption" color="textSecondary">YTD:</Typography>
            <Typography variant="body2" fontWeight="bold">
                {`${stats.ytdReturn.toFixed(2)}%`}
            </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <ShowChartIcon fontSize="small" color="primary" />
            <Typography variant="caption" color="textSecondary">Volume:</Typography>
            <Typography variant="body2" fontWeight="bold">
                {`$${(stats.volume / 1000000).toFixed(1)}M`}
            </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <FiberManualRecordIcon
                fontSize="small"
                sx={{ color: fund.status?.toLowerCase() === 'open' ? 'green' : 'orange' }}
            />
            <Typography variant="caption" color="textSecondary">Status:</Typography>
            <Typography variant="body2" fontWeight="bold">
                {fund.status || "Open"}
            </Typography>
        </Box>
    </Box>
);


export const FundCard = (props: { record?: Fund }) => {
    const [elevation, setElevation] = useState(1);
    const createPath = useCreatePath();
    const record = useRecordContext<Fund>(props);

    if (!record) return null;

    // Dummy stats data since we don't have these properties in the Fund type yet
    const dummyStats: FundStatsType = {
        ytdReturn: 8.4,
        dailyChange: 0.31,
        currentValuePerShare: 42.75,
        volume: 8500000
    };

    // Merge actual fund data with dummy stats
    // const stats = { ...dummyStats, ...record };

    return (
        <Link
            to={createPath({
                resource: 'funds',
                id: record.id,
                type: 'show',
            })}
            underline="none"
            onMouseEnter={() => setElevation(3)}
            onMouseLeave={() => setElevation(1)}
            sx={{ width: '100%', display: 'block' }}
        >
            <Card
                sx={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    padding: '0',
                    marginBottom: '8px',
                }}
            >
                <CardContent sx={{
                    padding: '0',
                    paddingBottom: '0',
                    "&:last-child": { paddingBottom: 0 },
                }}>
                    <Box
                        display="flex"
                        flexDirection="column"
                        sx={{
                            height: 400,
                            paddingBottom: '0',
                        }}
                    >

                        <Box sx={{
                            padding: '16px',
                            paddingBottom: '0',
                            backgroundColor: "action.hover",
                        }}>
                            {/* Header with fund name and current price */}
                            <FundCardHeader fund={record} stats={dummyStats} />

                            {/* Stats row */}
                            <FundStats fund={record} stats={dummyStats} />
                        </Box>

                        {/* Performance Chart - now with fixed height to ensure visibility */}
                        <FundChart fund={record} />
                    </Box>
                </CardContent>
            </Card>
        </Link >
    );
};